import { isApiError } from "@sagot/api-client";
import { SLA_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, EmptyState, Icon, Skeleton, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { formatMinutes } from "../../../engage/inbox/sla";
import { isPlanGateError, UpgradeCard, useSlaMutations, useSlaPlan, useSlaPolicies, type SlaPolicy } from "../../shared";
import { Gate, PanelError, PanelForbidden, TableScroll, useCanManage } from "./panel-kit";
import { SlaDialog } from "./sla-dialog";

const S = "settings.sla";

type Dialogs = { kind: "edit"; policy: SlaPolicy | null } | { kind: "delete"; policy: SlaPolicy } | null;

function DeleteSlaDialog({ policy, onClose }: { policy: SlaPolicy; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { remove } = useSlaMutations();
  const [inUse, setInUse] = useState(false);
  const confirm = () => {
    remove.mutate(policy.id, {
      onSuccess: () => {
        toast.success(t(`${S}.deleted`));
        onClose();
      },
      onError: (caught) => {
        if (isApiError(caught) && caught.status === 409) setInUse(true);
        else toast.error(message(caught));
      },
    });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${S}.deleteTitle`, { name: policy.name })}
        description={t(`${S}.deleteBody`)}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="danger" loading={remove.isPending} disabled={inUse} onClick={confirm}>{t(`${S}.delete`)}</Button>
          </>
        }
      >
        {inUse ? (
          <p className="flex items-start gap-2 rounded-md bg-warning-soft p-3 text-sm" role="alert" data-testid="sla-in-use">
            <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
            {t(`${S}.inUse`)}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function PolicyRow({ policy, locked, onDialog }: { policy: SlaPolicy; locked: boolean; onDialog: (dialog: Dialogs) => void }) {
  const { t } = useTranslation();
  const target = (minutes: number | null) => (minutes === null ? "—" : formatMinutes(minutes));
  return (
    <tr data-testid="sla-row">
      <td className="font-semibold">{policy.name}</td>
      <td className="tabular-nums">{target(policy.first_response_minutes)}</td>
      <td className="tabular-nums">{target(policy.next_response_minutes)}</td>
      <td className="tabular-nums">{target(policy.resolution_minutes)}</td>
      <td>{policy.office_hours_only ? <Badge tone="brand"><Icon name="clock" className="size-3" />{t(`${S}.officeOnly`)}</Badge> : <Badge>{t(`${S}.always`)}</Badge>}</td>
      <td className="tabular-nums">{formatMinutes(policy.warn_before_minutes)}</td>
      <td className="whitespace-nowrap text-right">
        <Button variant="ghost" size="sm" disabled={locked} aria-label={t(`${S}.editOne`, { name: policy.name })} onClick={() => { onDialog({ kind: "edit", policy }); }}>
          <Icon name="pencil" />
          {t(`${S}.edit`)}
        </Button>
        <Button variant="ghost" size="sm" iconOnly className="text-danger" disabled={locked} aria-label={t(`${S}.deleteOne`, { name: policy.name })} onClick={() => { onDialog({ kind: "delete", policy }); }}>
          <Icon name="trash" />
        </Button>
      </td>
    </tr>
  );
}

function PolicyTable({ policies, locked, onDialog }: { policies: SlaPolicy[]; locked: boolean; onDialog: (dialog: Dialogs) => void }) {
  const { t } = useTranslation();
  return (
    <TableScroll label={t(`${S}.title`)}>
      <table className="table">
        <thead>
          <tr>
            <th>{t(`${S}.name`)}</th>
            <th>{t(`${S}.firstResponse`)}</th>
            <th>{t(`${S}.nextResponse`)}</th>
            <th>{t(`${S}.resolution`)}</th>
            <th>{t(`${S}.clock`)}</th>
            <th>{t(`${S}.warnBefore`)}</th>
            <th><span className="sr-only">{t(`${S}.actions`)}</span></th>
          </tr>
        </thead>
        <tbody>{policies.map((policy) => <PolicyRow key={policy.id} policy={policy} locked={locked} onDialog={onDialog} />)}</tbody>
      </table>
    </TableScroll>
  );
}

/** Settings → SLA policies (Institution): reusable response and resolution targets for the Apply SLA step. */
export function SlaPanel() {
  const { t } = useTranslation();
  const { ready, blocked } = useSlaPlan();
  const list = useSlaPolicies(ready && !blocked);
  const manage = useCanManage("settings.manage");
  const [dialog, setDialog] = useState<Dialogs>(null);
  if (blocked || isPlanGateError(list.error)) return <UpgradeCard icon="gauge" title={t(`${S}.gateTitle`)} body={t(`${S}.gateBody`)} />;
  if (isApiError(list.error) && list.error.isForbidden) return <PanelForbidden />;
  if (list.isError) return <PanelError error={list.error} onRetry={() => void list.refetch()} />;
  const atLimit = (list.data?.length ?? 0) >= SLA_LIMITS.policies;
  const addButton = (
    <Gate reason={manage.reason ?? (atLimit ? t(`${S}.limit`, { max: SLA_LIMITS.policies }) : undefined)}>
      <Button size="sm" disabled={!manage.allowed || atLimit} onClick={() => { setDialog({ kind: "edit", policy: null }); }}>
        <Icon name="plus" />
        {t(`${S}.add`)}
      </Button>
    </Gate>
  );
  return (
    <>
      <section className="card overflow-hidden" aria-labelledby="sla-title">
        <div className="card-header">
          <div>
            <h2 id="sla-title" className="card-title">{t(`${S}.title`)}</h2>
            <p className="mt-0.5 text-xs text-fg-subtle">{t(`${S}.subtitle`)}</p>
          </div>
          {addButton}
        </div>
        {!ready || list.isPending ? (
          <div className="space-y-3 p-5" aria-hidden="true">
            <Skeleton className="h-5 w-[360px] max-w-full" />
            <Skeleton className="h-5 w-[240px]" />
          </div>
        ) : list.data.length === 0 ? (
          <EmptyState icon="gauge" headingLevel={3} title={t(`${S}.emptyTitle`)} description={t(`${S}.emptyBody`)} action={addButton} />
        ) : (
          <PolicyTable policies={list.data} locked={!manage.allowed} onDialog={setDialog} />
        )}
      </section>
      {dialog?.kind === "edit" ? <SlaDialog policy={dialog.policy} onClose={() => { setDialog(null); }} /> : null}
      {dialog?.kind === "delete" ? <DeleteSlaDialog policy={dialog.policy} onClose={() => { setDialog(null); }} /> : null}
    </>
  );
}
